import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Map as MapIcon, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DraggablePin } from './DraggablePin';

export function MapViewer({
  currentMap,
  filteredPins,
  zoom,
  pan,
  isDragging,
  editMode,
  isMJ,
  language,
  loading,
  getPinIcon,
  getPinColor,
  onPanChange,
  onZoomChange,
  onMapClick,
  onPinClick,
  onPinDoubleClick,
  onPinMove,
  onNavigateToLinkedMap
}) {
  const containerRef = useRef(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [localDragging, setLocalDragging] = useState(false);
  const lastTouchDistanceRef = useRef(null);
  const isPinchingRef = useRef(false);
  const clickStartRef = useRef(null);
  
  // Mouse pan handling
  const handleMouseDown = useCallback((e) => {
    // Only pan if:
    // - In navigation mode (click drag pans)
    // - In edit mode with middle mouse button or alt key
    if (!editMode || e.button === 1 || e.altKey) {
      setLocalDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      clickStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    } else if (editMode && e.button === 0) {
      // In edit mode, track click start for potential new pin
      clickStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
    }
  }, [editMode, pan]);

  const handleMouseMove = useCallback((e) => {
    if (localDragging) {
      onPanChange({ 
        x: e.clientX - dragStart.x, 
        y: e.clientY - dragStart.y 
      });
    }
  }, [localDragging, dragStart, onPanChange]);

  const handleMouseUp = useCallback((e) => {
    const wasClick = clickStartRef.current && 
      Math.abs(e.clientX - clickStartRef.current.x) < 5 &&
      Math.abs(e.clientY - clickStartRef.current.y) < 5 &&
      Date.now() - clickStartRef.current.time < 300;
    
    if (wasClick && editMode && isMJ && !localDragging) {
      // This was a click in edit mode - create new pin
      onMapClick(e);
    }
    
    setLocalDragging(false);
    clickStartRef.current = null;
  }, [editMode, isMJ, localDragging, onMapClick]);

  // Touch handling
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch zoom start
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastTouchDistanceRef.current = dist;
      isPinchingRef.current = true;
    } else if (e.touches.length === 1) {
      // Single finger - pan
      isPinchingRef.current = false;
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
      setLocalDragging(true);
      clickStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    }
  }, [pan]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && isPinchingRef.current) {
      // Pinch zoom
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      
      if (lastTouchDistanceRef.current) {
        const scale = dist / lastTouchDistanceRef.current;
        const newZoom = Math.min(3, Math.max(0.5, zoom * scale));
        onZoomChange(newZoom);
      }
      
      lastTouchDistanceRef.current = dist;
    } else if (e.touches.length === 1 && localDragging && !isPinchingRef.current) {
      // Pan
      const touch = e.touches[0];
      onPanChange({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  }, [zoom, localDragging, dragStart, onPanChange, onZoomChange]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      // Check if it was a tap (for adding pin in edit mode)
      if (clickStartRef.current && editMode && isMJ) {
        const lastTouch = e.changedTouches[0];
        const wasClick = 
          Math.abs(lastTouch.clientX - clickStartRef.current.x) < 10 &&
          Math.abs(lastTouch.clientY - clickStartRef.current.y) < 10 &&
          Date.now() - clickStartRef.current.time < 300;
        
        if (wasClick && !isPinchingRef.current) {
          // Simulate click event for pin creation
          const syntheticEvent = {
            clientX: lastTouch.clientX,
            clientY: lastTouch.clientY,
            preventDefault: () => {},
            stopPropagation: () => {},
            nativeEvent: { offsetX: lastTouch.clientX, offsetY: lastTouch.clientY }
          };
          onMapClick(syntheticEvent);
        }
      }
      
      setLocalDragging(false);
      isPinchingRef.current = false;
      lastTouchDistanceRef.current = null;
      clickStartRef.current = null;
    }
  }, [editMode, isMJ, onMapClick]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(3, Math.max(0.5, zoom + delta));
    onZoomChange(newZoom);
  }, [zoom, onZoomChange]);

  // Attach wheel listener with passive: false
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentMap) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className={cn(
        "flex-1 relative overflow-hidden bg-muted/30",
        localDragging && "cursor-grabbing",
        !localDragging && !editMode && "cursor-grab",
        !localDragging && editMode && isMJ && "cursor-crosshair"
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setLocalDragging(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Current Map Name Badge */}
      <div className="absolute top-3 left-3 z-10">
        <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm shadow-md">
          <MapIcon className="h-3 w-3 mr-1" />
          {currentMap.name}
        </Badge>
      </div>

      {/* Map Image with Pins */}
      <div
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center',
          transition: localDragging ? 'none' : 'transform 0.1s ease'
        }}
        className="w-full h-full flex items-center justify-center"
      >
        <div className="relative map-pins-container">
          <img 
            src={currentMap.image_url} 
            alt={currentMap.name}
            className="max-w-full max-h-[calc(100vh-14rem)] object-contain"
            draggable={false}
          />
          
          {/* Pins */}
          {filteredPins.map(pin => {
            const Icon = getPinIcon(pin.pin_type);
            const color = getPinColor(pin.pin_type);
            
            return (
              <DraggablePin
                key={pin.id}
                pin={pin}
                Icon={Icon}
                color={color}
                language={language}
                isMJ={isMJ}
                editMode={editMode}
                onPinClick={onPinClick}
                onPinDoubleClick={onPinDoubleClick}
                onPinDragEnd={onPinMove}
                onNavigateToLinkedMap={onNavigateToLinkedMap}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
